/**
 * Zayma — Product image zoom
 * ---------------------------------------------------------------------------
 * A single custom element that adds, to the EXISTING Dawn product gallery:
 *
 *   • desktop hover magnifier lens (optionally with a detail preview panel)
 *   • a minimal zoom icon on each product image
 *   • a full-screen viewer with pinch / double-tap zoom, pan and swipe
 *
 * It never rebuilds the gallery. Every interaction is delegated from the
 * document and resolved through `closest()` at event time, so images added,
 * replaced or reordered by a variant change (product-info.js `updateMedia`)
 * and even a full product swap keep working — no re-binding, no stale node
 * references and never a second listener on the same element.
 *
 * Only image media inside the main product gallery is touched. Video,
 * external video and 3D model containers are ignored, quick-add and featured
 * product sections are out of scope, and Dawn's own media modal is left
 * completely intact.
 *
 * Rendered from sections/zayma-product-image-zoom.liquid (Footer group), on
 * product pages only, and only when "Enable Product Image Zoom" is on.
 */

if (!customElements.get('product-image-zoom')) {
  const MAX_ZOOM_WIDTH = 3000; // px — Shopify returns the original if smaller
  const MIN_ZOOM_WIDTH = 1200;
  const VIEWER_MAX_SCALE = 4;

  /* ------------------------------- helpers -------------------------------- */

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  /**
   * Ask the Shopify CDN for a wider rendition of the image that is already on
   * screen. Never upscales: if the original is narrower, Shopify returns the
   * original. Non-Shopify URLs are returned untouched.
   */
  function highResUrl(src, width) {
    if (!src) return src;
    try {
      const url = new URL(src, window.location.href);
      if (!/cdn\.shopify\.com$/.test(url.hostname) && !/\/cdn\/shop\//.test(url.pathname)) return src;
      url.searchParams.set('width', String(Math.round(width)));
      url.searchParams.delete('height');
      url.searchParams.delete('crop');
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  /**
   * The rectangle the image actually paints inside its box, honouring
   * object-fit (the theme's "media fit" setting can be contain or cover).
   */
  function paintedRect(img) {
    const box = img.getBoundingClientRect();
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    if (!nw || !nh || !box.width || !box.height) return box;

    const fit = window.getComputedStyle(img).objectFit;
    let scale;
    if (fit === 'contain' || fit === 'scale-down') {
      scale = Math.min(box.width / nw, box.height / nh);
    } else if (fit === 'cover') {
      scale = Math.max(box.width / nw, box.height / nh);
    } else {
      return box;
    }

    const width = nw * scale;
    const height = nh * scale;
    const left = box.left + (box.width - width) / 2;
    const top = box.top + (box.height - height) / 2;
    return { left, top, width, height, right: left + width, bottom: top + height };
  }

  /** The gallery of the main product section (never a quick-add or a card). */
  const MAIN_GALLERY = 'product-info[id^="MainProduct"] media-gallery';

  function inMainGallery(element) {
    return !!element && !!element.closest(MAIN_GALLERY);
  }

  class ProductImageZoom extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;

      this.strength = parseFloat(this.dataset.strength) || 2;
      this.styleMode = this.dataset.zoomStyle === 'preview' ? 'preview' : 'lens';
      this.wantIcon = this.dataset.icon === 'true';
      this.allowPinch = this.dataset.pinch !== 'false';
      this.openOnClick = this.dataset.clickOpen === 'true';
      this.zoomLabel = this.dataset.labelZoom || 'Zoom product image';
      this.closeLabel = this.dataset.labelClose || 'Close';

      this.initialised = true;

      this.desktopQuery = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.sources = new WeakMap();
      this.pointers = new Map();
      this.viewer = null;
      this.activeImage = null;
      this.frame = null;

      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onCaptureClick = this.onCaptureClick.bind(this);
      this.onWindowChange = this.onWindowChange.bind(this);

      document.addEventListener('pointermove', this.onPointerMove, { passive: true });
      document.addEventListener('pointercancel', this.onPointerLeave, { passive: true });
      // Capture phase: this is what lets the fullscreen viewer take over the
      // image click without touching Dawn's modal-opener code.
      document.addEventListener('click', this.onCaptureClick, true);
      window.addEventListener('resize', this.onWindowChange, { passive: true });
      window.addEventListener('scroll', this.onWindowChange, { passive: true });

      if (this.wantIcon) {
        this.decorate();
        // Watches for gallery images swapped in by a variant change or a
        // product swap. Debounced, and it only ever reads the gallery.
        this.observer = new MutationObserver(() => {
          window.clearTimeout(this.decorateTimer);
          this.decorateTimer = window.setTimeout(() => this.decorate(), 120);
        });
        this.observer.observe(document.querySelector('main') || document.body, {
          childList: true,
          subtree: true,
        });
      }
    }

    disconnectedCallback() {
      if (!this.initialised) return;
      this.hideLens();
      this.observer?.disconnect();
      window.clearTimeout(this.decorateTimer);
      document.removeEventListener('pointermove', this.onPointerMove);
      document.removeEventListener('pointercancel', this.onPointerLeave);
      document.removeEventListener('click', this.onCaptureClick, true);
      window.removeEventListener('resize', this.onWindowChange);
      window.removeEventListener('scroll', this.onWindowChange);
      if (this.keyHandler) document.removeEventListener('keydown', this.keyHandler);
      this.panel?.remove();
      this.panel = null;
      this.viewer?.remove();
      this.viewer = null;
      document.documentElement.classList.remove('zpz-locked');
      this.initialised = false;
    }

    /* ------------------------------- icons -------------------------------- */

    /** Adds the zoom icon to any image container that does not have one yet. */
    decorate() {
      document.querySelectorAll(`${MAIN_GALLERY} .product-media-container.media-type-image`).forEach((container) => {
        if (container.querySelector(':scope > .zpz-icon')) return;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'zpz-icon';
        button.setAttribute('aria-label', this.zoomLabel);
        button.setAttribute('aria-haspopup', 'dialog');
        const icon = this.querySelector('template');
        if (icon) button.appendChild(icon.content.cloneNode(true));
        container.appendChild(button);
      });
    }

    /* -------------------------------- lens -------------------------------- */

    onPointerMove(event) {
      if (!this.desktopQuery.matches || event.pointerType === 'touch') return;
      this.lastPointer = { x: event.clientX, y: event.clientY, target: event.target };
      if (this.frame) return;
      this.frame = window.requestAnimationFrame(() => {
        this.frame = null;
        this.trackPointer();
      });
    }

    onPointerLeave() {
      this.hideLens();
    }

    onWindowChange() {
      if (this.activeImage) this.hideLens();
    }

    trackPointer() {
      const pointer = this.lastPointer;
      if (!pointer) return;

      const container =
        pointer.target instanceof Element
          ? pointer.target.closest('.product-media-container.media-type-image')
          : null;
      if (!container || !inMainGallery(container)) return this.hideLens();

      const media = container.querySelector('.product__media');
      const image = media && media.querySelector('img');
      if (!media || !image || !image.complete || !image.naturalWidth) return this.hideLens();

      const painted = paintedRect(image);
      if (
        pointer.x < painted.left ||
        pointer.x > painted.right ||
        pointer.y < painted.top ||
        pointer.y > painted.bottom
      ) {
        return this.hideLens();
      }

      const lens = this.ensureLens();
      if (lens.parentElement !== media) media.appendChild(lens);
      this.activeImage = image;

      // Detail preview, when the layout has genuine room for it beside the
      // gallery. Otherwise this silently stays "lens only" — never overlapping
      // the product information column and never shifting the layout.
      const panel = this.styleMode === 'preview' ? this.placePanel(painted, container) : null;

      let lensWidth;
      let lensHeight;
      if (panel) {
        lensWidth = panel.width / this.strength;
        lensHeight = panel.height / this.strength;
      } else {
        const size = clamp(Math.min(painted.width, painted.height) * 0.36, 120, 280);
        lensWidth = size;
        lensHeight = size;
      }
      lensWidth = Math.min(lensWidth, painted.width);
      lensHeight = Math.min(lensHeight, painted.height);

      // Position of the pointer inside the painted image.
      const pointerX = pointer.x - painted.left;
      const pointerY = pointer.y - painted.top;
      const lensX = clamp(pointerX - lensWidth / 2, 0, Math.max(0, painted.width - lensWidth));
      const lensY = clamp(pointerY - lensHeight / 2, 0, Math.max(0, painted.height - lensHeight));

      const mediaRect = media.getBoundingClientRect();
      const source = this.zoomSource(image, painted.width);
      const backgroundWidth = painted.width * this.strength;
      const backgroundHeight = painted.height * this.strength;
      const backgroundPosition = `${-lensX * this.strength}px ${-lensY * this.strength}px`;
      const backgroundSize = `${backgroundWidth}px ${backgroundHeight}px`;

      lens.style.width = `${lensWidth}px`;
      lens.style.height = `${lensHeight}px`;
      lens.style.transform = `translate3d(${painted.left - mediaRect.left + lensX}px, ${
        painted.top - mediaRect.top + lensY
      }px, 0)`;
      this.paint(lens, source, backgroundSize, backgroundPosition);
      lens.classList.add('zpz-is-visible');

      if (panel) {
        const element = this.ensurePanel();
        element.style.width = `${panel.width}px`;
        element.style.height = `${panel.height}px`;
        element.style.transform = `translate3d(${panel.left}px, ${panel.top}px, 0)`;
        this.paint(
          element,
          source,
          `${backgroundWidth}px ${backgroundHeight}px`,
          `${-lensX * this.strength}px ${-lensY * this.strength}px`
        );
        element.classList.add('zpz-is-visible');
      } else if (this.panel) {
        this.panel.classList.remove('zpz-is-visible');
      }
    }

    paint(element, source, size, position) {
      if (source && element.dataset.src !== source) {
        element.style.backgroundImage = `url("${source}")`;
        element.dataset.src = source;
      }
      element.style.backgroundSize = size;
      element.style.backgroundPosition = position;
    }

    ensureLens() {
      if (!this.lens) {
        this.lens = document.createElement('div');
        this.lens.className = 'zpz-lens';
        this.lens.setAttribute('aria-hidden', 'true');
      }
      return this.lens;
    }

    ensurePanel() {
      if (!this.panel) {
        this.panel = document.createElement('div');
        this.panel.className = 'zpz-panel';
        this.panel.setAttribute('aria-hidden', 'true');
        document.body.appendChild(this.panel);
      }
      return this.panel;
    }

    hideLens() {
      this.activeImage = null;
      this.lens?.classList.remove('zpz-is-visible');
      this.panel?.classList.remove('zpz-is-visible');
    }

    /**
     * Free space beside the whole product grid — never on top of the product
     * information column, so the title, price and Add to cart can not be
     * covered and nothing moves.
     */
    placePanel(painted, container) {
      const grid = container.closest('.product') || container.closest(MAIN_GALLERY);
      if (!grid) return null;
      const rect = grid.getBoundingClientRect();
      const gap = 24;
      const minWidth = 320;
      const maxWidth = 560;
      const viewportWidth = document.documentElement.clientWidth;

      const spaceRight = viewportWidth - rect.right - gap * 2;
      const spaceLeft = rect.left - gap * 2;

      let width;
      let left;
      if (spaceRight >= minWidth) {
        width = Math.min(spaceRight, maxWidth);
        left = rect.right + gap;
      } else if (spaceLeft >= minWidth) {
        width = Math.min(spaceLeft, maxWidth);
        left = rect.left - gap - width;
      } else {
        return null; // automatic fallback to "Lens Only"
      }

      const ratio = painted.height / painted.width;
      let height = width * ratio;
      const maxHeight = window.innerHeight - gap * 2;
      if (height > maxHeight) {
        height = maxHeight;
        width = height / ratio;
      }

      const top = clamp(painted.top, gap, Math.max(gap, window.innerHeight - height - gap));
      return { width, height, left, top };
    }

    /**
     * Higher-resolution source for the hovered image. Nothing is requested
     * until the shopper actually hovers; until the sharper file arrives the
     * lens uses the image already on screen, so it never flashes empty.
     */
    zoomSource(image, paintedWidth) {
      const wanted = clamp(
        Math.round(paintedWidth * this.strength * Math.min(window.devicePixelRatio || 1, 2)),
        MIN_ZOOM_WIDTH,
        MAX_ZOOM_WIDTH
      );
      let record = this.sources.get(image);

      if (!record || record.wanted < wanted) {
        const url = highResUrl(image.currentSrc || image.src, wanted);
        record = { wanted, ready: record ? record.ready : image.currentSrc || image.src };
        this.sources.set(image, record);

        const preload = new Image();
        preload.decoding = 'async';
        preload.onload = () => {
          record.ready = url;
          // Repaint straight away if the shopper is still on this image and
          // has stopped moving, so the lens sharpens without another move.
          if (this.activeImage === image) {
            if (this.lens) this.lens.dataset.src = '';
            if (this.panel) this.panel.dataset.src = '';
            this.trackPointer();
          }
        };
        preload.onerror = () => {
          record.ready = record.ready || image.currentSrc || image.src;
        };
        preload.src = url;
      }

      return record.ready;
    }

    /* ------------------------------- opening ------------------------------ */

    onCaptureClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const icon = target.closest('.zpz-icon');
      const toggle = icon ? null : target.closest('.product__media-toggle');
      if (!icon && !toggle) return;

      const container = target.closest('.product-media-container');
      if (!container || !container.classList.contains('media-type-image')) return;
      if (!inMainGallery(container)) return;

      // Image clicks only take over when the merchant has asked for it; the
      // zoom icon always opens the viewer.
      if (toggle && !this.openOnClick) return;

      event.preventDefault();
      event.stopPropagation();
      this.open(container, icon || toggle);
    }

    /* ------------------------------- viewer ------------------------------- */

    slides(gallery) {
      return Array.from(gallery.querySelectorAll('li[data-media-id]'))
        .map((item) => {
          const container = item.querySelector('.product-media-container');
          if (!container || !container.classList.contains('media-type-image')) return null;
          const image = container.querySelector('.product__media img');
          if (!image) return null;
          return { id: item.dataset.mediaId, src: image.currentSrc || image.src, alt: image.alt || '' };
        })
        .filter(Boolean);
    }

    buildViewer() {
      if (this.viewer) return this.viewer;

      const viewer = document.createElement('div');
      viewer.className = 'zpz-viewer';
      viewer.setAttribute('role', 'dialog');
      viewer.setAttribute('aria-modal', 'true');
      viewer.setAttribute('aria-label', this.zoomLabel);
      viewer.hidden = true;
      viewer.innerHTML = [
        '<div class="zpz-viewer__track"></div>',
        '<button type="button" class="zpz-viewer__close">',
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">',
        '<path d="M18.3 5.71 12 12l6.3 6.29-1.41 1.42L10.59 13.4 4.3 19.71 2.88 18.3 9.17 12 2.88 5.71 4.3 4.29l6.29 6.3 6.3-6.3z"></path>',
        '</svg></button>',
        '<div class="zpz-viewer__counter" aria-hidden="true"></div>',
      ].join('');

      this.track = viewer.querySelector('.zpz-viewer__track');
      this.counter = viewer.querySelector('.zpz-viewer__counter');
      this.closeButton = viewer.querySelector('.zpz-viewer__close');
      this.closeButton.setAttribute('aria-label', this.closeLabel);

      this.closeButton.addEventListener('click', () => this.close());
      viewer.addEventListener('pointerdown', (e) => this.onViewerDown(e));
      viewer.addEventListener('pointermove', (e) => this.onViewerMove(e));
      viewer.addEventListener('pointerup', (e) => this.onViewerUp(e));
      viewer.addEventListener('pointercancel', (e) => this.onViewerUp(e));
      viewer.addEventListener('wheel', (e) => this.onViewerWheel(e), { passive: false });
      this.keyHandler = (e) => {
        if (!this.viewer || this.viewer.hidden) return;
        this.onViewerKey(e);
      };
      document.addEventListener('keydown', this.keyHandler);

      document.body.appendChild(viewer);
      this.viewer = viewer;
      return viewer;
    }

    open(container, opener) {
      const gallery = container.closest(MAIN_GALLERY);
      if (!gallery) return;
      const media = this.slides(gallery);
      if (!media.length) return;

      const item = container.closest('li[data-media-id]');
      const index = Math.max(
        0,
        media.findIndex((entry) => entry.id === item?.dataset.mediaId)
      );

      this.hideLens();
      this.opener = opener || document.activeElement;
      const viewer = this.buildViewer();

      const width = Math.min(
        MAX_ZOOM_WIDTH,
        Math.round(
          Math.max(window.innerWidth, window.innerHeight) * Math.min(window.devicePixelRatio || 1, 2) * 1.4
        )
      );

      this.track.innerHTML = '';
      this.media = media;
      media.forEach((entry) => {
        const slide = document.createElement('div');
        slide.className = 'zpz-slide';
        const image = document.createElement('img');
        image.alt = entry.alt;
        image.decoding = 'async';
        image.draggable = false;
        image.dataset.src = highResUrl(entry.src, width);
        slide.appendChild(image);
        this.track.appendChild(slide);
      });

      this.scrollY = window.scrollY;
      document.documentElement.classList.add('zpz-locked');
      viewer.hidden = false;
      // Next frame, so the opacity transition runs.
      window.requestAnimationFrame(() => viewer.classList.add('zpz-is-open'));

      this.index = index;
      this.resetTransform(false);
      this.goTo(index, false);
      this.closeButton.focus({ preventScroll: true });
    }

    close() {
      if (!this.viewer || this.viewer.hidden) return;
      this.viewer.classList.remove('zpz-is-open');
      this.pointers.clear();
      const finish = () => {
        this.viewer.hidden = true;
        this.track.innerHTML = '';
      };
      if (this.reduceMotion.matches) finish();
      else window.setTimeout(finish, 180);

      document.documentElement.classList.remove('zpz-locked');
      window.scrollTo(0, this.scrollY || 0);
      const opener = this.opener;
      if (opener && document.contains(opener)) opener.focus({ preventScroll: true });
      this.opener = null;
    }

    /** Loads only the current slide and its neighbours. */
    hydrate() {
      Array.from(this.track.children).forEach((slide, position) => {
        const image = slide.querySelector('img');
        if (!image) return;
        if (Math.abs(position - this.index) <= 1 && !image.src) image.src = image.dataset.src;
      });
    }

    goTo(index, animate = true) {
      // Leave every other slide at rest, so returning to it starts unzoomed.
      Array.from(this.track.querySelectorAll('img')).forEach((image) => {
        image.style.transition = 'none';
        image.style.transform = '';
      });
      this.index = clamp(index, 0, this.media.length - 1);
      this.resetTransform(false);
      this.track.style.transition = animate && !this.reduceMotion.matches ? 'transform 0.28s ease' : 'none';
      this.track.style.transform = `translate3d(${-this.index * 100}%, 0, 0)`;
      this.counter.textContent = `${this.index + 1} / ${this.media.length}`;
      this.counter.hidden = this.media.length < 2;
      this.hydrate();
    }

    currentImage() {
      return this.track.children[this.index]?.querySelector('img') || null;
    }

    resetTransform(animate = true) {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.applyTransform(animate);
    }

    applyTransform(animate = false) {
      const image = this.currentImage();
      if (!image) return;
      image.style.transition = animate && !this.reduceMotion.matches ? 'transform 0.22s ease' : 'none';
      image.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
      image.style.cursor = this.scale > 1 ? 'grab' : 'zoom-in';
    }

    /** Keeps the image from being dragged away from the viewport. */
    clampPan() {
      const image = this.currentImage();
      if (!image) return;
      const width = image.offsetWidth * this.scale;
      const height = image.offsetHeight * this.scale;
      const maxX = Math.max(0, (width - window.innerWidth) / 2);
      const maxY = Math.max(0, (height - window.innerHeight) / 2);
      this.tx = clamp(this.tx, -maxX, maxX);
      this.ty = clamp(this.ty, -maxY, maxY);
    }

    zoomAt(scale, clientX, clientY) {
      const image = this.currentImage();
      if (!image) return;
      const next = clamp(scale, 1, VIEWER_MAX_SCALE);
      const rect = image.getBoundingClientRect();
      const centreX = rect.left + rect.width / 2;
      const centreY = rect.top + rect.height / 2;
      const ratio = next / this.scale;
      this.tx = clientX - centreX - (clientX - centreX - this.tx) * ratio;
      this.ty = clientY - centreY - (clientY - centreY - this.ty) * ratio;
      this.scale = next;
      if (this.scale === 1) {
        this.tx = 0;
        this.ty = 0;
      }
      this.clampPan();
      this.applyTransform(true);
    }

    onViewerDown(event) {
      if (event.target.closest('.zpz-viewer__close')) return;
      this.viewer.setPointerCapture?.(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.allowPinch) {
        const [a, b] = Array.from(this.pointers.values());
        this.gesture = {
          mode: 'pinch',
          distance: Math.hypot(a.x - b.x, a.y - b.y) || 1,
          scale: this.scale,
          tx: this.tx,
          ty: this.ty,
          midX: (a.x + b.x) / 2,
          midY: (a.y + b.y) / 2,
        };
        return;
      }

      if (this.pointers.size === 1) {
        this.gesture = {
          mode: this.scale > 1 ? 'pan' : 'swipe',
          x: event.clientX,
          y: event.clientY,
          tx: this.tx,
          ty: this.ty,
          time: Date.now(),
          axis: null,
          moved: false,
        };
        this.track.style.transition = 'none';
      }
    }

    onViewerMove(event) {
      if (!this.gesture || !this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.gesture.mode === 'pinch' && this.pointers.size >= 2) {
        const [a, b] = Array.from(this.pointers.values());
        const distance = Math.hypot(a.x - b.x, a.y - b.y) || 1;
        const next = clamp((distance / this.gesture.distance) * this.gesture.scale, 1, VIEWER_MAX_SCALE);
        const ratio = next / this.gesture.scale;
        const image = this.currentImage();
        if (image) {
          const rect = image.getBoundingClientRect();
          const centreX = rect.left + rect.width / 2;
          const centreY = rect.top + rect.height / 2;
          this.tx = this.gesture.midX - centreX - (this.gesture.midX - centreX - this.gesture.tx) * ratio;
          this.ty = this.gesture.midY - centreY - (this.gesture.midY - centreY - this.gesture.ty) * ratio;
        }
        this.scale = next;
        this.clampPan();
        this.applyTransform(false);
        return;
      }

      const dx = event.clientX - this.gesture.x;
      const dy = event.clientY - this.gesture.y;
      if (!this.gesture.moved && Math.hypot(dx, dy) > 4) this.gesture.moved = true;

      if (this.gesture.mode === 'pan') {
        this.tx = this.gesture.tx + dx;
        this.ty = this.gesture.ty + dy;
        this.clampPan();
        this.applyTransform(false);
        return;
      }

      if (this.gesture.mode === 'swipe') {
        if (!this.gesture.axis && Math.hypot(dx, dy) > 8) {
          this.gesture.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }
        if (this.gesture.axis !== 'x') return;
        this.track.style.transform = `translate3d(calc(${-this.index * 100}% + ${dx}px), 0, 0)`;
      }
    }

    onViewerUp(event) {
      this.pointers.delete(event.pointerId);
      const gesture = this.gesture;
      if (!gesture) return;

      if (gesture.mode === 'pinch') {
        if (this.pointers.size === 0) {
          if (this.scale <= 1.02) this.resetTransform(true);
          this.gesture = null;
        } else {
          const remaining = Array.from(this.pointers.values())[0];
          this.gesture = {
            mode: this.scale > 1 ? 'pan' : 'swipe',
            x: remaining.x,
            y: remaining.y,
            tx: this.tx,
            ty: this.ty,
            time: Date.now(),
            axis: null,
            moved: true,
          };
        }
        return;
      }

      if (this.pointers.size > 0) return;
      this.gesture = null;

      if (gesture.mode === 'swipe') {
        const dx = event.clientX - gesture.x;
        const quick = Date.now() - gesture.time < 300 && Math.abs(dx) > 40;
        if (gesture.axis === 'x' && (Math.abs(dx) > window.innerWidth * 0.22 || quick)) {
          this.goTo(this.index + (dx < 0 ? 1 : -1), true);
        } else {
          this.goTo(this.index, true);
        }
      }

      if (!gesture.moved) this.handleTap(event);
    }

    handleTap(event) {
      // A tap on the empty area around the image closes the viewer.
      if (event.target instanceof Element && !event.target.closest('img')) {
        this.close();
        return;
      }

      const now = Date.now();
      const isDouble =
        this.lastTap &&
        now - this.lastTap.time < 320 &&
        Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 30;

      this.lastTap = { time: now, x: event.clientX, y: event.clientY };
      if (!isDouble) return;
      this.lastTap = null;
      if (!this.allowPinch) return;
      this.zoomAt(this.scale > 1 ? 1 : 2.5, event.clientX, event.clientY);
    }

    onViewerWheel(event) {
      if (!this.allowPinch) return;
      event.preventDefault();
      this.zoomAt(this.scale * (1 - event.deltaY * 0.0018), event.clientX, event.clientY);
    }

    onViewerKey(event) {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          this.close();
          break;
        case 'ArrowRight':
          event.preventDefault();
          this.goTo(this.index + 1, true);
          break;
        case 'ArrowLeft':
          event.preventDefault();
          this.goTo(this.index - 1, true);
          break;
        case 'Tab':
          // Only the close control is focusable inside the viewer, so focus
          // stays where it belongs until the shopper closes it.
          event.preventDefault();
          this.closeButton.focus({ preventScroll: true });
          break;
        default:
          break;
      }
    }
  }

  customElements.define('product-image-zoom', ProductImageZoom);
}